import {
  FormMessage,
  LayoutContainer,
  MiniLink,
  PageSection,
  PageSectionContent,
  RteWrapper,
} from '@greystar/designsystem';
import { Property } from '@greystar/lib';
import { CommunityContactFormProps, Form } from '@greystar/ui';
import { FormActionResponse } from '@greystar/ui/src/components/form-builder/form/FormTypes';

import React, { JSX } from 'react';
import { GetStaticComponentProps, withDatasourceCheck } from '@sitecore-jss/sitecore-jss-nextjs';
import { getEmailTemplateConfig } from '../../lib/send-grid/email-template-helper';
import { getCommunityContactForm } from '../../lib/data/edge/community-contact-form-query';

const buildAction =
  ({ propertyId, formId, language }: { propertyId?: string; formId?: string; language: string }) =>
  async (formData: FormData): Promise<FormActionResponse> => {
    const response = await fetch('/api/pdp/forms/community-contact-form', {
      method: 'POST',
      body: JSON.stringify({
        propertyId,
        formId,
        language,
        formData: Object.fromEntries(formData),
      }),
    });
    try {
      const result = await response.json();
      return result;
    } catch (err) {
      return { success: false, error: err as Error };
    }
  };

const CommunityContactForm = (props: CommunityContactFormProps): JSX.Element => {
  if (!props.data) {
    return <></>;
  }

  const emailTemplate = getEmailTemplateConfig({
    trackingEmail: props.data.propertyTrackingEmail,
    emailTemplates: props.fields.emailTemplates,
  });

  const emailOverride =
    emailTemplate?.fields?.emailOverride?.value || props.fields.emailOverride.value;

  return (
    <PageSection id="contact-us">
      <PageSectionContent>
        <PageSectionContent hasMaxWidth className="pt-4 md:pt-0 max-w-[61rem] mx-0">
          <LayoutContainer flexDirection="column">
            <LayoutContainer flexDirection="column" gap="md" paddingBottom="md" className="pb-4">
              <RteWrapper>
                <div>
                  {props.fields.heading.value && (
                    <h2 className="!mb-2">{props.fields.heading.value}</h2>
                  )}
                  {props.fields.description.value && (
                    <p className="!mb-0 lg:!mb-[10px] ">{props.fields.description.value}</p>
                  )}
                  {props.data.propertyPhone && (
                    <MiniLink
                      variant="textPlain"
                      href={`tel:${props.data.propertyPhone}`}
                      label={<em>{props.data.propertyPhone}</em>}
                      icon={{ position: 'left', name: 'Phone' }}
                      className="pb-0"
                    />
                  )}
                </div>
              </RteWrapper>
            </LayoutContainer>
            {emailOverride && (
              <LayoutContainer paddingBottom="lg" aria-hidden={true}>
                <FormMessage
                  text="Email override in place"
                  text2={emailOverride}
                  variant={'error'}
                />
              </LayoutContainer>
            )}
            <Form
              props={props}
              callbacks={{
                action: buildAction({
                  propertyId: props.data.propertyId,
                  formId: props.rendering.dataSource,
                  language: props.data.language ?? 'en',
                }),
              }}
            />
          </LayoutContainer>
        </PageSectionContent>
      </PageSectionContent>
    </PageSection>
  );
};

export const getStaticProps: GetStaticComponentProps = async (
  rendering,
  layoutData
): Promise<CommunityContactFormProps> => {
  const props = rendering as unknown as CommunityContactFormProps;
  const property = layoutData.sitecore.context.property as Property;
  const language = layoutData.sitecore.context.language;

  if (!property) {
    return {
      ...props,
      data: {
        notFound: true,
      },
    };
  }
  const propertyCountry = property.location?.countryCode ?? '';
  let formProps = props;
  if (
    props.fields.country?.value?.trim()?.toLocaleLowerCase() !==
    propertyCountry?.trim()?.toLocaleLowerCase()
  ) {
    formProps = (await getCommunityContactForm({ request: { country: propertyCountry } })) ?? props;
  }

  return {
    ...formProps,
    data: {
      notFound: false,
      propertyId: property.id,
      propertyTrackingEmail: property.contact?.trackingEmail ?? null,
      propertyPhone:
        property?.contact?.trackingPhoneNumber ?? property?.contact?.phoneNumber ?? null,
      propertyName: property.name ?? null,
      propertyLocation: property.location ?? null,
      language,
    },
  };
};

export default withDatasourceCheck()<CommunityContactFormProps>(CommunityContactForm);
